import type { GraphStructure } from './scopesGraph';

export const APPROVED_PARENTS = [
  'ComputeCluster',
  'Nodepool',
  'Annotation',
  'Collaborator',
  'Collector',
  'Concept',
  'Input',
  'Model',
  'Predict',
  'Search',
  'Task',
  'Workflow',
  'Other',
];
export const PARENT_NAME_MAP: { [key: string]: string } = {
  App: 'App',
  AnnotationsStatus: 'Annotation',
  ComputeClusters: 'ComputeCluster',
  Nodepools: 'Nodepools',
  Collaborator: 'Collaborator',
  Collector: 'Collector',
  ConceptsSearch: 'Concept',
  ConceptLanguage: 'Concept',
  ConceptRelation: 'Concept',
  ConceptReference: 'Concept',
  ConceptCount: 'Concept',
  InputsFile: 'Input',
  InputCount: 'Input',
  Key: 'Key',
  ModelInput: 'Input',
  ModelOutput: 'Predict',
  ModelOutputInfo: 'Model',
  ModelVersion: 'Model',
  ModelVersionMetric: 'Model',
  ModelsSearch: 'Model',
  SearchesByID: 'Search',
  Task: 'Task',
  Template: 'Model',
  WorkflowResult: 'Predict',
};
export const BASIC_DESCRIPTIONS: { [key: string]: string } = {
  ComputeCluster: 'ComputeCluster: Add, Remove and Patch Compute Clusters',
  Nodepool: 'Nodepool: Add, Remove and Patch Nodepools',
  Annotation: 'Annotation: Add and Remove Annotations on Inputs',
  App: 'App: Add and Remove Annotations on Inputs',
  Collaborator: 'Collaborator: Add, Remove and Patch Collaborators',
  Collector: 'Collector: Add, Remove and Patch Input Collectors',
  Concept: 'Concept: Add, Remove and Patch Concept',
  Input: 'Input: Add and Remove Inputs to an App',
  Key: 'API Keys: Add, Remove and Patch API Keys',
  Metric: 'Metric: Add, Remove and Delete Evaluations Metrics',
  Model: 'Model: Add, Remove and Train Custom Models',
  Predict: 'Predict: Predict on Public and Custom Models',
  Search: 'Search: Search over Inputs an App',
  Task: 'Task: Add, Remove and Patch Scribe labeling Tasks',
  Workflow: 'Workflow: Add, Remove and Delete Workflows of Models',
  Other: 'Others: Unsorted',
};
export const NAME_SUFFIX = '_TOP';

export const OTHER_GROUP = 'Other_TOP';

// Quick fix for: https://clarifai.atlassian.net/browse/MRK-3729
export const HARDCODED_VIEW_APP_SCOPES = ['Apps:Get', '/clarifai.api.V2/GetApp'];

export const SCOPE_GROUPS_LIST = APPROVED_PARENTS.map((group) => `${group}${NAME_SUFFIX}`);

export const scopesGraphUtils = {
  /**
   * Remove the prefix from the names that match HTTP methods like PostInputs -> Inputs
   * @param {string} name the vertex name.
   * @return {string} the stripped suffix.
   */
  parseAndStripHTTP(name: string): string {
    return name.replace(/^(Get|List|Post|Patch|Delete)/, '');
  },

  /**
   * Removes trailing "es" and "s" while handling some special cases.
   * @param {string} name
   * @return {string} name - singular version of name.
   */
  makeSingular(name: string): string {
    if (
      name.endsWith('es') &&
      !name.endsWith('Languages') &&
      !name.endsWith('Licenses') &&
      !name.endsWith('MyScopes') &&
      !name.endsWith('References') &&
      !name.endsWith('Templates')
    ) {
      name = name.slice(0, -2);
    } else if (name.endsWith('s') && !name.endsWith('Status')) {
      name = name.slice(0, -1);
    }
    return name;
  },

  /**
   * Makes the endpoint non-plural, then maps some common endings to their proper parents.
   * @param {string} parent - name of the vertex.
   * @return {string} parent name for grouping vertices.
   */
  cleanParentName(parent: string): string {
    // Make non-plural
    parent = this.makeSingular(parent);
    // Hacky map from the resulting parent scope to what we want the parent scope to be.
    // This is useful for grouping things in the UI like the various concept knowledge graph
    // endpoints under "Concept" or multiple undisclosed endpoints under 'Other'.
    if (parent in PARENT_NAME_MAP) {
      return PARENT_NAME_MAP[parent];
    }

    const approvedParentsList = [
      'All',
      'ComputeCluster',
      'Nodepool',
      'Annotation',
      'Concept',
      'Input',
      'Model',
      'Predict',
      'Search',
      'Worker',
      'Workflow',
    ];
    if (!approvedParentsList.includes(parent)) {
      return 'Other';
    }
    return parent;
  },

  /**
   * Get the parent name from the two types of scope/endpoint names used to group vertices.
   * For example:
   * Inputs:Add -> Input
   * /clarifai.api.V2/PostInputs -> Input
   * @param {string} name of the vertex.
   * @return {string} parent name for grouping vertices.
   */
  getCleanedParentName(name: string): string {
    name = name?.replace(new RegExp(NAME_SUFFIX), '');

    // endpoint type of name like /clarifai.api.V2/PostInputs
    if (name && name?.startsWith('/')) {
      const prefix = '/clarifai.api.V2/';
      const suffix = name.replace(prefix, '');
      const remaining = this.parseAndStripHTTP(suffix);
      return this.cleanParentName(remaining);
    }
    if (name && name.includes(':')) {
      // scope type of name like Inputs:Add
      const remaining = name.match(/[^:]*/i)?.[0];
      return this.cleanParentName(remaining);
    }
    return name;
  },

  /**
   * Add edge between corresponding nodes. Since top-level roots
   * (Predict, Search, Inputs, Concepts, Models) are connected
   * with their <scope>:<action> counterparts, add edge in both
   * directions.
   * we add a link from _TOP and All node to the scopeName.
   * @param graph instance of Graph
   * @param {string} permissionName the vertex name.
   * @param {string} parentName the stripped suffix.
   */
  addDependencyOrReverseDependencies(graph: GraphStructure, permissionName: string, parentName: string): void {
    graph.addEdge(`${parentName}${NAME_SUFFIX}`, permissionName);
    graph.addEdge('All', permissionName);
    graph.addEdge('All', `${parentName}${NAME_SUFFIX}`);
  },
};
